import { cn } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { availabilityQuery } from "@/features/availability";
import {
  BasketBar,
  FeaturedTreatment,
  PriceCards,
  publicCatalogQuery,
  publicStaffQuery,
  RitualsMenu,
  TeamStrip,
} from "@/features/booking";
import { LastVisitCard } from "@/features/my-appointments";
import {
  AnnouncementsBand,
  HomeHero,
  SalonHighlights,
  salonLoadError,
  salonNow,
  salonProfileQuery,
  useSalonClock,
  VisitSection,
} from "@/features/salon";
import { auth } from "@/lib/api";
import { ProblemCard } from "@/shared/components/ProblemCard";
import { sectionGap } from "@/shared/styles/layout";
import { homeContentQuery } from "../api/home.queries";
import { ArticlesTeaser } from "../components/ArticlesTeaser";
import { GallerySection } from "../components/GallerySection";
import { HomeSkeleton } from "../components/HomeSkeleton";
import { homeStats } from "../components/HomeStats";
import { TestimonialsSection } from "../components/TestimonialsSection";

/**
 * The salon's own page, as a landing page (design note salon-home-landing.md section 5; TDD-SA
 * FR-SA-004, FR-SA-138, FR-SA-190), in the owner's Organic Wellness style (design note
 * salon-booking-organic-wellness.md section 6; FR-SA-202), in the board's order: the hero with the
 * salon in numbers, what is on, what it stands for, its menu to open and add from, its team on the green
 * band, its journal, kind words, its pictures, and how to find it.
 *
 * What the salon wrote for its page (FR-SA-191) comes first, section by section, and a section it
 * switched off is gone. Where it wrote nothing, the section falls back to its real data; if its
 * content cannot be read at all, the whole page does, rather than failing.
 *
 * Built from data every salon has: the profile, the menu and the team. A section whose data is
 * missing or failed is left out, never shown empty; only the profile itself is essential. Services
 * added here travel into booking through `?services=`, so nothing is kept in a store.
 */
export function HomePage() {
  const profile = useQuery(salonProfileQuery());
  const catalog = useQuery(publicCatalogQuery());
  const staff = useQuery(publicStaffQuery());
  const home = useQuery(homeContentQuery());
  const clock = useSalonClock();
  const [picked, setPicked] = useState<string[]>([]);
  const signedIn = auth.useIsSignedIn();

  const menu = (catalog.data ?? []).filter((category) => category.services.length > 0);
  const staffList = staff.data ?? [];
  const services = menu.reduce((count, category) => count + category.services.length, 0);

  // "Next free 12:30" on the hero: the first time left today for the salon's first service.
  const probe = menu[0]?.services[0]?.id;
  const today = useQuery(
    availabilityQuery({
      serviceIds: probe ? [probe] : [],
      from: salonNow(clock.timeZone).date,
      days: 1,
    }),
  );
  const firstToday = today.data?.[0]?.slots[0]?.startsAt;

  if (profile.isError) {
    const problem = salonLoadError(profile.error);
    return (
      <div className="mx-auto w-full max-w-md px-4 py-16">
        <ProblemCard
          headingLevel={1}
          title={problem.title}
          description={problem.description}
          tone={problem.refusal ? "refusal" : "problem"}
          onRetry={problem.canRetry ? () => void profile.refetch() : undefined}
        />
      </div>
    );
  }

  if (profile.isPending || home.isPending) return <HomeSkeleton />;

  const salon = profile.data;
  const content = home.data ?? { latestArticles: [], galleryItemCount: 0 };
  const shows = (section: { visible: boolean } | undefined) => section?.visible ?? true;
  // The hero's round frame and the visit's arch come from what the salon shows further down, never from a section it switched off.
  const gallery = shows(content.gallery) ? (content.gallery?.images ?? []) : [];
  const plans = shows(content.prices) && content.prices?.mode === "plans" ? content.prices.plans : [];
  // The menu to open and add from is the services and the price list in one (section 6 item 5).
  const menuShown = shows(content.services) || (shows(content.prices) && plans.length === 0);
  // The hero's round frame: the picture the salon chose (FR-SA-258), else (section 10.3) a gallery picture, else
  // the first menu picture that is not one of the hero's own.
  const heroImages = content.hero?.images ?? [];
  const heroPictures = new Set(heroImages.length > 0 ? heroImages.map((image) => image.url) : [salon.coverImageUrl]);
  const menuPictures = [
    ...Object.values(content.services?.categoryImages ?? {}),
    ...menu.flatMap((category) => category.services.flatMap((service) => (service.imageUrl ? [{ url: service.imageUrl, altText: "" }] : []))),
  ].filter((image) => !heroPictures.has(image.url));
  const inset = content.hero?.inset ?? gallery[0] ?? menuPictures[0] ?? null;
  // The featured treatment (FR-SA-203) shows only for a service the salon chose; the team band runs on from it.
  const chosen = shows(content.featured) ? content.featured : undefined;
  const featured =
    chosen?.serviceId && menu.some((category) => category.services.some((service) => service.id === chosen.serviceId))
      ? chosen
      : null;
  const team = shows(content.team) ? staffList : [];
  const toggle = (id: string) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  return (
    // The hero's blob and the gallery strip reach past the gutter; the page clips them, never scrolls sideways.
    <div className={cn("animate-arrive flex flex-col overflow-x-clip pt-6 pb-6 lg:pt-10", sectionGap)}>
      <div className="flex flex-col gap-10 lg:gap-16">
        <HomeHero
          salon={salon}
          categories={menu.map((category) => category.name)}
          nextFree={firstToday ? clock.time(firstToday) : null}
          lastVisit={signedIn ? <LastVisitCard /> : null}
          custom={content.hero ?? null}
          inset={inset}
          stats={shows(content.about) ? homeStats(salon, staffList.length, services, content.about ?? null) : []}
        />
        <AnnouncementsBand />
      </div>
      {shows(content.whyUs) ? (
        <SalonHighlights
          name={salon.displayName}
          facilities={salon.facilities}
          paymentOptions={salon.paymentOptions}
          heading={content.whyUs?.heading ?? (shows(content.about) ? content.about?.heading : null)}
          intro={shows(content.about) ? content.about?.text : null}
          items={content.whyUs?.items}
        />
      ) : null}
      {menuShown ? (
        <RitualsMenu
          catalog={menu}
          selected={picked}
          onToggle={toggle}
          heading={content.services?.heading ?? content.prices?.heading}
          images={content.services?.categoryImages}
          texts={content.services?.categoryTexts}
        />
      ) : null}
      {plans.length > 0 ? (
        <PriceCards catalog={menu} selected={picked} onToggle={toggle} heading={content.prices?.heading} plans={plans} />
      ) : null}
      {featured || team.length > 0 ? (
        <div>
          {featured ? (
            <FeaturedTreatment
              catalog={menu}
              featured={featured}
              selected={picked}
              onToggle={toggle}
              joined={team.length > 0}
            />
          ) : null}
          <TeamStrip staff={team} heading={content.team?.heading} joined={Boolean(featured)} />
        </div>
      ) : null}
      {shows(content.articles) ? (
        <ArticlesTeaser
          heading={content.articles?.heading ?? null}
          text={content.articles?.text ?? null}
          articles={content.latestArticles}
        />
      ) : null}
      {shows(content.testimonials) ? (
        <TestimonialsSection
          heading={content.testimonials?.heading ?? null}
          items={content.testimonials?.items ?? []}
        />
      ) : null}
      {shows(content.gallery) ? (
        <GallerySection
          heading={content.gallery?.heading ?? null}
          text={content.gallery?.text ?? null}
          images={content.gallery?.images ?? []}
          viewAll={content.galleryItemCount > 0}
        />
      ) : null}
      <VisitSection salon={salon} image={gallery[1] ?? heroImages[0] ?? null} />
      <BasketBar catalog={menu} selected={picked} />
    </div>
  );
}
